import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CourierAssignmentFilters,
  CourierAssignmentFormData,
  CourierFormData,
} from "../Types";
import {
  assignCourierToOrder,
  deleteCourier,
  getCourierAssignments,
  getCouriers,
  saveCourier,
  unassignCourier,
  updateCourierAssignmentStatus,
} from "../Api";

export const courierQueryKeys = {
  all: ["couriers"] as const,
  assignments: ["courier-assignments"] as const,
};

export const useCourierQuery = () => {
  return useQuery({
    queryKey: courierQueryKeys.all,
    queryFn: () => getCouriers(),
  });
};

export const useCourierSaveMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CourierFormData) => saveCourier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
    },
  });
};

export const useCourierDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCourier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
    },
  });
};

export const useCourierAssignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CourierAssignmentFormData) => assignCourierToOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
    },
  });
};

export const useCourierAssignmentStatusUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      assignment_id: string;
      status: string;
      notes?: string;
    }) => updateCourierAssignmentStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
    },
  });
};

export const useCourierUnassignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignment_id: string) => unassignCourier(assignment_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
    },
  });
};

export const useCourierAssignmentsQuery = (
  filters: CourierAssignmentFilters = {},
) => {
  return useQuery({
    queryKey: [...courierQueryKeys.assignments, filters],
    queryFn: () => getCourierAssignments(filters),
  });
};
